import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { retryPendingAcks } from './adapter';
import { type MimoClient, mimoClient } from './client';
import { defaultPreferences, displayMimoError, MAX_REFERENCE_BYTES, MimoError, MODE_LABELS, PRESET_VOICES } from './contracts';
import type { TtsAccount, TtsPlans, VoiceMode, VoicePreferences } from './contracts';
import { type MimoStorage, mimoStorage } from './storage';
import type { LocalReference } from './storage';
import { MimoElement as E } from './RegisteredElement';
import styles from './mimo.module.css';
import { getDb } from '../../core/db';
import { loadVoiceDisplayPreferences, saveVoiceDisplayPreferences, type VoiceDisplayPreferences } from './displayPreferences';
import { modelGateway } from '../modelGateway';

export interface TtsPageProps {
  client?: MimoClient;
  storage?: MimoStorage;
  onAccountChange?: (account: TtsAccount | null) => void;
}
function useBlobUrl(blob: Blob | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) { setUrl(undefined); return; }
    const next = URL.createObjectURL(blob); setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}
export function TtsPage({ client = mimoClient, storage = mimoStorage, onAccountChange }: TtsPageProps = {}): ReactElement {
  const [display, setDisplay] = useState<VoiceDisplayPreferences>({ waitForAudio: false, voiceBubble: false });
  const [plans, setPlans] = useState<TtsPlans>();
  const [account, setAccount] = useState<TtsAccount | null>(null);
  const [preferences, setPreferences] = useState<VoicePreferences>(defaultPreferences);
  const [tab, setTab] = useState<VoiceMode>('preset');
  const [reference, setReference] = useState<LocalReference>();
  const [consent, setConsent] = useState(false);
  const [text, setText] = useState('你好，很高兴与你交谈。');
  const [preview, setPreview] = useState<Blob>();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const lock = useRef(false);
  const generation = useRef(0);
  const callback = useRef(onAccountChange); callback.current = onAccountChange;
  const previewUrl = useBlobUrl(preview);
  const referenceUrl = useBlobUrl(reference?.blob);

  async function installAccount(next: TtsAccount | null): Promise<void> {
    setAccount(next); callback.current?.(next);
    setDisplay(loadVoiceDisplayPreferences(next?.userId));
    setPreferences(next?.preferences ?? defaultPreferences());
    setReference(next ? await storage.reference(next.userId) : undefined);
    setConsent(false); setPreview(undefined);
  }
  async function load(): Promise<void> {
    const token = ++generation.current;
    setLoading(true); setError(undefined);
    const [publicResult, accountResult] = await Promise.allSettled([client.plans(), client.account()]);
    if (token !== generation.current) return;
    if (publicResult.status === 'fulfilled') setPlans(publicResult.value);
    else setError(displayMimoError(publicResult.reason));
    try {
      if (accountResult.status === 'fulfilled') {
        await installAccount(accountResult.value);
        await retryPendingAcks(accountResult.value.userId, client, storage);
      } else {
        await installAccount(null);
        setError(displayMimoError(accountResult.reason));
      }
    } catch (failure) { setError(displayMimoError(failure)); }
    finally { if (token === generation.current) setLoading(false); }
  }
  useEffect(() => { void load(); return () => { generation.current++; }; }, [client, storage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!account) return;
    const retry = () => { void retryPendingAcks(account.userId, client, storage).catch(() => undefined); };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [account, client, storage]);

  async function run(action: () => Promise<void>): Promise<void> {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(undefined); setNotice(undefined);
    try { await action(); }
    catch (failure) { setError(displayMimoError(failure)); }
    finally { lock.current = false; setBusy(false); }
  }
  async function save(): Promise<TtsAccount> {
    if (!account) throw new MimoError('请先登录');
    if (JSON.stringify(preferences) === JSON.stringify(account.preferences)) return account;
    const result = await client.savePreferences(preferences);
    const next = { ...account, preferences: result.preferences };
    setAccount(next); callback.current?.(next);
    setDisplay(loadVoiceDisplayPreferences(next?.userId));
    return next;
  }
  function updateDisplay(patch: Partial<VoiceDisplayPreferences>): void {
    if (!account) return;
    const next = { ...display, ...patch };
    try { saveVoiceDisplayPreferences(account.userId, next); setDisplay(next); }
    catch { setError('显示偏好保存失败，请检查设备存储空间'); }
  }
  const disabled = busy || loading;
  return <E meta="root" as="main" className={styles.page} data-skin-surface="page" aria-busy={disabled}>
    <E meta="title" as="h1">MiMo 语音</E>
    <E meta="account" as="p" className={styles.muted}>{loading ? '正在加载账户' : account ? account.active ? account.expiresAt === null ? '官方套餐语音功能可用' : `官方套餐语音可用至 ${new Date(account.expiresAt).toLocaleString()}` : '当前套餐不支持语音功能' : '请先登录'}</E>
    <E meta="refresh" as="button" type="button" disabled={disabled} onClick={() => void run(load)}>刷新账户</E>
    <E meta="plans" as="section" className={styles.section} aria-label="语音套餐状态">
      <E meta="planName" as="h2">官方套餐语音</E>
      <E meta="planPrice" as="p" className={styles.muted}>
        语音订阅已并入官方套餐。{account?.quota ? `套餐语音额度：${account.quota.remaining === null ? '不限量' : `${account.quota.used.toLocaleString()} / ${account.quota.limit?.toLocaleString() ?? '不限量'} 字符`}` : '请到设置页查看官方套餐。'}
      </E>
    </E>
    <E meta="displaySettings" as="section" className={styles.section} aria-label="语音回复显示">
      <E meta="displayTitle" as="h2">语音回复显示</E>
      <E meta="waitLabel" as="label" className={styles.displaySwitch}>语音生成后再显示文字
        <E meta="waitToggle" as="input" type="checkbox" role="switch" aria-label="语音生成后再显示文字" checked={display.waitForAudio} disabled={disabled || !account} onChange={event => updateDisplay({ waitForAudio: event.currentTarget.checked })} />
      </E>
      <E meta="bubbleLabel" as="label" className={styles.displaySwitch}>启用语音气泡
        <E meta="bubbleToggle" as="input" type="checkbox" role="switch" aria-label="启用语音气泡" checked={display.voiceBubble} disabled={disabled || !account || !display.waitForAudio} onChange={event => updateDisplay({ voiceBubble: event.currentTarget.checked })} />
      </E>
      <E meta="displayHint" as="p" className={`${styles.muted} ${styles.displayHint}`}>先开启等待语音，语音气泡才会生效。点击气泡播放，长按选择“查看文字”；自动朗读沿用会话中的开关。</E>
    </E>
    <E meta="settings" as="section" className={styles.section} aria-label="独立音色设置">
      <E meta="tabs" as="div" className={styles.segmented} role="tablist" aria-label="音色设置">
        {(['preset', 'design', 'clone'] as const).map((mode) => <E meta="tab" instance={mode} key={mode} as="button" type="button" role="tab" aria-selected={tab === mode} disabled={disabled} onClick={() => setTab(mode)}>{MODE_LABELS[mode]}</E>)}
      </E>
      {tab === 'preset' ? <>
        <E meta="presetLabel" as="label" htmlFor="mimo-preset">预设音色</E>
        <E meta="preset" as="select" id="mimo-preset" value={preferences.preset.voice} disabled={disabled || !account} onChange={(event) => { const voice = event.currentTarget.value as VoicePreferences['preset']['voice']; setPreferences((previous) => ({ ...previous, preset: { voice } })); }}>
          {(plans?.voices ?? PRESET_VOICES).map((voice) => <E meta="presetOption" instance={voice} as="option" key={voice} value={voice}>{voice}</E>)}
        </E>
      </> : tab === 'design' ? <>
        <E meta="designLabel" as="label" htmlFor="mimo-design">音色设计描述</E>
        <E meta="design" as="textarea" id="mimo-design" rows={4} maxLength={2000} value={preferences.design.description} disabled={disabled || !account} onChange={(event) => { const description = event.currentTarget.value; setPreferences((previous) => ({ ...previous, design: { description } })); }} />
      </> : <>
        <E meta="cloneLabel" as="label" htmlFor="mimo-clone">复刻指令描述</E>
        <E meta="clone" as="textarea" id="mimo-clone" rows={4} maxLength={2000} value={preferences.clone.instruction} disabled={disabled || !account} onChange={(event) => { const instruction = event.currentTarget.value; setPreferences((previous) => ({ ...previous, clone: { instruction } })); }} />
        <E meta="consentLabel" as="label" className={styles.check} htmlFor="mimo-consent">
          <E meta="consent" as="input" id="mimo-consent" type="checkbox" checked={consent} disabled={disabled || !account} onChange={(event) => setConsent(event.currentTarget.checked)} />
          我已获得此音色的复刻授权，同意仅在复刻合成时上传参考音频。
        </E>
        <E meta="referenceLabel" as="label" htmlFor="mimo-reference">参考音频（MP3 / WAV，最大 {(Math.min(plans?.maxReferenceBytes ?? MAX_REFERENCE_BYTES, MAX_REFERENCE_BYTES) / 1048576).toFixed(1)} MB）</E>
        <E meta="reference" as="input" id="mimo-reference" type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" disabled={disabled || !account || !consent} onChange={(event) => {
          const file = event.currentTarget.files?.[0]; event.currentTarget.value = '';
          if (file && account) void run(async () => { setReference(await storage.saveReference(account.userId, file, consent, plans?.maxReferenceBytes)); setNotice('参考音频已保存在此设备'); });
        }} />
        {reference ? <>
          <E meta="referenceName" as="p">此设备参考音频：{reference.name}</E>
          <E meta="referenceAudio" as="audio" controls src={referenceUrl} preload="none" aria-label="参考音频" />
          <E meta="removeReference" as="button" type="button" disabled={disabled} onClick={() => void run(async () => {
            if (!account || !window.confirm('仅删除此设备的参考音频？复刻指令和聊天语音将保留。')) return;
            await storage.removeReference(account.userId); setReference(undefined); setConsent(false);
          })}>删除参考音频</E>
        </> : null}
      </>}
      <E meta="save" as="button" type="button" disabled={disabled || !account} onClick={() => void run(async () => { await save(); setNotice('音色设置已保存'); })}>保存音色设置</E>
    </E>
    <E meta="preview" as="section" className={styles.section} aria-label="试听">
      <E meta="previewLabel" as="label" htmlFor="mimo-preview-text">试听文本</E>
      <E meta="previewText" as="textarea" id="mimo-preview-text" rows={3} maxLength={plans?.maxTextChars ?? 4000} value={text} disabled={disabled || !account} onChange={(event) => setText(event.currentTarget.value)} />
      <E meta="synthesize" as="button" type="button" disabled={disabled || !account?.active || !text.trim() || (tab === 'clone' && !reference)} onClick={() => void run(async () => {
        const saved = await save();
        const adapter = modelGateway.createOfficialTtsAdapter({ client, storage, preview: true, getSelection: () => ({ userId: saved.userId, mode: tab, preferences: saved.preferences }) });
        try { setPreview((await adapter.speak({ text })).audio); }
        catch (failure) { throw new MimoError(failure instanceof Error ? failure.message : displayMimoError(failure)); }
      })}>生成试听</E>
      {preview ? <E meta="previewAudio" as="audio" controls src={previewUrl} preload="none" aria-label="试听音频" /> : null}
    </E>
    <E meta="cleanup" as="button" type="button" disabled={disabled || !account} onClick={() => void run(async () => {
      if (!account) return;
      await retryPendingAcks(account.userId, client, storage);
      const messageIds = new Set(await getDb().messages.toCollection().primaryKeys());
      const count = await storage.cleanup(account.userId, Date.now() - 86400000, messageIds); setPreview(undefined);
      await client.clearTemporary();
      setNotice(`已清理 ${count} 条未关联试听或过期音频，聊天语音、参考音频和设置已保留`);
    })}>清理未关联试听及过期音频</E>
    <E meta="retry" as="button" type="button" disabled={disabled || !account} onClick={() => void run(async () => {
      if (!account) return;
      const count = await retryPendingAcks(account.userId, client, storage);
      const pending = await storage.db.pendingAcks.where('userId').equals(account.userId).count();
      setNotice(`已确认 ${count} 条音频${pending ? `，${pending} 条待重试` : ''}`);
    })}>重试音频保存确认</E>
    {error || notice ? <E meta="status" as="p" className={error ? styles.error : styles.muted} role={error ? 'alert' : 'status'}>{error ?? notice}</E> : null}
  </E>;
}
